import DOMPurify, { type Config } from "dompurify";

/**
 * Rich-text-only profile for HTML we don't author: CRM note bodies, emails.
 *
 * - Tags: block and inline formatting, lists, links, headings, tables, images.
 *   No forms or form controls, `<style>`, frames or plugins.
 * - Attributes: href, title, src, alt, target, rel, colspan, rowspan only.
 *   `style` and `class` are both out: with Tailwind on the page,
 *   `class="fixed inset-0 z-50 bg-white"` rebuilds a full-page overlay with no
 *   inline style at all. `data-*` attributes are out too.
 * - URLs: http, https, mailto and tel only (so no `javascript:` or `data:`).
 * - Links that open a new tab always get `rel="noopener noreferrer"`.
 */
const RICH_TEXT_CONFIG: Config = {
  // ALLOWED_URI_REGEXP is checked against every attribute that isn't URI-safe,
  // so these plain-value attributes would otherwise be dropped.
  ADD_URI_SAFE_ATTR: ["target", "rel", "colspan", "rowspan"],
  ALLOW_DATA_ATTR: false,
  ALLOWED_ATTR: ["href", "title", "src", "alt", "target", "rel", "colspan", "rowspan"],
  ALLOWED_TAGS: [
    "p",
    "br",
    "span",
    "div",
    "strong",
    "b",
    "em",
    "i",
    "u",
    "s",
    "ul",
    "ol",
    "li",
    "blockquote",
    "pre",
    "code",
    "a",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "hr",
    "img",
  ],
  ALLOWED_URI_REGEXP: /^(?:https?|mailto|tel):/i,
  FORBID_TAGS: [
    "form",
    "input",
    "button",
    "select",
    "textarea",
    "option",
    "style",
    "iframe",
    "object",
    "embed",
  ],
};

// A dedicated instance, because DOMPurify hooks are global to an instance:
// the rel hook below must not leak into other DOMPurify users (renderChatMarkdown).
const purifier = DOMPurify(window);
purifier.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.hasAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

/**
 * Sanitize an HTML string before it is bound with `v-html`, using the
 * rich-text profile described on RICH_TEXT_CONFIG above.
 */
export function sanitizeHtml(html: string): string {
  return purifier.sanitize(html, RICH_TEXT_CONFIG);
}

/**
 * Escape a value for interpolation into an HTML string. Use this on every
 * dynamic value (names, campaign titles) before building markup for
 * `v-html`; keep `sanitizeHtml` at the sink as a second layer.
 */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
